<template>
  <div>
    <div class="qr-auth-container">
      <!-- 状态显示 -->
      <div class="status-info">
        <p>{{ $t('authenticationModal.tip') }}</p>
        <div class="status-text">{{ statusText }}</div>
        <div class="countdown" v-if="countdown > 0">
          {{
            $t('authenticationModal.countdownTip', {
              countdown: formatTime(countdown),
            })
          }}
        </div>
      </div>

      <div v-if="status === 'expired'" class="mask-container">
        <a-button
          @click="refreshQRCode"
          :loading="refreshing"
          icon="redo"
        ></a-button>
      </div>
      <!-- 二维码 -->
      <div class="qrcode-container">
        <a-icon type="loading" v-if="isLoading" />
        <div class="qrcode" ref="QRcodeRef" v-else></div>
        <div class="qrcode-tip">
          <p>{{ $t('authenticationModal.qrcodeTip') }}</p>
          <p>
            {{ $t('authenticationModal.qrcodeTip2') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wechatGetUrl, wechatScanStatus } from '@/apis/qrcodeAuthentication'
import { message } from 'ant-design-vue'
import QRCode from 'qrcode'

export default {
  name: 'GQRcodeAuthentication',
  props: {
    isShowQRcode: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '扫码验证',
    },
    data: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      confirmLoading: false,
      refreshing: false,
      countdown: 300, // 5分钟倒计时
      status: 'waiting', // waiting, scanning, success, error, expired
      state: '',
      pollTimer: null,
      countdownTimer: null,
      // 状态文本映射
      statusTextMap: {
        waiting: this.$t('authenticationModal.waiting'),
        scanning: this.$t('authenticationModal.scanning'),
        success: this.$t('authenticationModal.success'),
        error: this.$t('authenticationModal.error'),
        expired: this.$t('authenticationModal.expired'),
      },
      isLoading: true,
    }
  },
  computed: {
    statusText() {
      return (
        this.statusTextMap[this.status] ||
        this.$t('authenticationModal.waiting')
      )
    },
  },
  watch: {
    isShowQRcode: {
      handler(newVal) {
        if (newVal) {
          this.initQRCode()
        } else {
          this.cleanup()
        }
      },
      immediate: true,
    },
  },
  methods: {
    // 创建验证请求
    async createAuthRequest() {
      const res = await wechatGetUrl({
        code_type: this.data.code_type,
      })
      if (res.status) {
        this.isLoading = false
        return {
          state: res.data.nonce_str,
          qrCodeUrl: res.data.wechat_scan_url,
        }
      } else {
        this.isLoading = false
        return {
          state: '',
          qrCodeUrl: '',
        }
      }
    },

    // 生成二维码
    async generateQRCode(url) {
      try {
        // 清空容器
        if (this.$refs.QRcodeRef) {
          this.$refs.QRcodeRef.innerHTML = ''
        }
        // 生成二维码到canvas
        const canvas = await QRCode.toCanvas(url, {
          width: 200,
          height: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        })

        // 将canvas添加到容器中
        if (this.$refs.QRcodeRef) {
          this.$refs.QRcodeRef.appendChild(canvas)
        }
      } catch (error) {
        console.error('生成二维码失败:', error)
        throw error
      }
    },

    // 开始倒计时
    startCountdown() {
      this.countdownTimer = window.setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          if (this.countdownTimer) {
            clearInterval(this.countdownTimer)
            this.countdownTimer = null
          }
          if (this.pollTimer) {
            clearInterval(this.pollTimer)
            this.pollTimer = null
          }
          this.status = 'expired'
          message.error('验证超时')
        }
      }, 1000)
    },

    // 开始轮询状态
    startPolling() {
      this.pollTimer = window.setInterval(async () => {
        try {
          const response = await this.checkAuthStatus()
          switch (response.status) {
            case 'waiting':
              break
            case 'success':
              if (this.pollTimer) {
                clearInterval(this.pollTimer)
                this.pollTimer = null
              }
              this.status = 'success'
              await this.handleAuthSuccess(this.state)
              break
            case 'expired':
              if (this.pollTimer) {
                clearInterval(this.pollTimer)
                this.pollTimer = null
              }
              this.status = 'expired'
              message.error('验证已过期')
              break
            case 'error':
              if (this.pollTimer) {
                clearInterval(this.pollTimer)
                this.pollTimer = null
              }
              this.status = 'error'
              this.cleanup()
              this.$emit('update:isShowQRcode', false)
              this.$emit('auth-error', this.state)
              message.error('验证失败：' + response.message)
              break
          }
        } catch (error) {
          console.error('检查验证状态失败:', error)
        }
      }, 2000)
    },

    // 初始化二维码
    async initQRCode() {
      try {
        this.isLoading = true
        this.confirmLoading = true
        this.status = 'waiting'
        this.countdown = 300

        // 创建验证请求
        const response = await this.createAuthRequest()
        this.state = response.state
        // 生成二维码
        await this.generateQRCode(response.qrCodeUrl)
        // 开始轮询状态
        this.startPolling()
        // 开始倒计时
        this.startCountdown()
      } catch (error) {
        console.error('初始化二维码失败:', error)
        this.status = 'error'
        message.error('初始化验证失败')
      } finally {
        this.confirmLoading = false
      }
    },

    // 检查验证状态
    async checkAuthStatus() {
      const res = await wechatScanStatus({
        nonce_str: this.state,
      })
      if (res.status) {
        // is_scanned 后端返回的是否扫码
        return {
          status: res.data.is_expired
            ? 'expired'
            : res.data.is_scanned
            ? 'success'
            : 'waiting',
          message: res.msg,
        }
      } else {
        return {
          status: 'error',
          message: res.msg,
        }
      }
    },

    // 处理验证成功
    async handleAuthSuccess(authData) {
      try {
        message.success('验证成功！')
        this.$emit('auth-success', authData)
        this.cleanup()
        this.$emit('update:isShowQRcode', false)
      } catch (error) {
        console.error('处理验证成功失败:', error)
        message.error('操作执行失败')
        this.cleanup()
        this.$emit('update:isShowQRcode', false)
        this.$emit('auth-error', error)
      }
    },

    // 刷新二维码
    async refreshQRCode() {
      this.refreshing = true
      try {
        this.cleanup()
        await this.initQRCode()
        message.success('二维码已刷新')
      } catch (error) {
        message.error('刷新失败')
      } finally {
        this.refreshing = false
      }
    },

    // 清理资源
    cleanup() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },

    // 格式化时间
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    },

    handleCancel() {
      this.cleanup()
      this.$emit('update:isShowQRcode', false)
      this.$emit('auth-cancel')
    },
  },
  beforeDestroy() {
    this.cleanup()
  },
}
</script>

<style scoped lang="scss">
.qr-auth-container {
  text-align: center;
  padding: 20px 0;
  position: relative;
}

.status-info {
  margin-bottom: 20px;

  .status-text {
    font-size: 16px;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
  }

  .countdown {
    font-size: 14px;
    color: #999;
  }
}
.mask-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.2);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
}

.qrcode-container {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  .qrcode-loading {
    width: 240px;
    height: 240px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 20px;
  }
  .qrcode {
    width: 240px;
    height: 240px;
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
    padding: 20px;
    background: #fff;
    border: 2px solid #e8e8e8;
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    &:hover {
      border-color: #18a8c7;
      box-shadow: 0 6px 16px rgba(24, 144, 255, 0.15);
      transform: translateY(-2px);
    }

    canvas {
      border-radius: 12px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
    }
  }

  .qrcode-tip {
    color: #666;
    font-size: 14px;
    line-height: 1.6;
    text-align: center;

    p {
      margin: 6px 0;
      font-weight: 500;

      &:first-child {
        color: #18a8c7;
        font-weight: 600;
      }
    }
  }
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;

  .ant-btn {
    min-width: 80px;
  }
}
</style>
